/**
 * Staff Clock - Mobile Time Clock App for Staff
 * Allows staff to register devices, clock in/out, and view their schedule
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, 
  Smartphone,
  Wifi,
  WifiOff,
  Play,
  Square,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Calendar,
  Timer,
  User,
  LogOut,
  Sun,
  Moon,
  ChevronRight,
  RefreshCw,
  Shield,
  Fingerprint
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Get or create a persistent unique device ID
// This ID is stored in the browser and persists until browser data is cleared
function getOrCreateDeviceId() {
  const STORAGE_KEY = 'bulaccino_device_id';
  let deviceId = localStorage.getItem(STORAGE_KEY);
  
  if (!deviceId) {
    // Generate a truly unique ID for this device installation
    // Uses crypto.randomUUID if available, otherwise fallback
    if (crypto.randomUUID) {
      deviceId = crypto.randomUUID();
    } else {
      // Fallback for older browsers
      deviceId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
      });
    }
    // Add timestamp to make it even more unique
    deviceId = deviceId + '-' + Date.now().toString(36);
    localStorage.setItem(STORAGE_KEY, deviceId);
  }
  
  return deviceId;
}

// Generate device fingerprint using persistent unique ID
function generateDeviceFingerprint() {
  // Get the persistent unique device ID - this is the primary identifier
  const persistentDeviceId = getOrCreateDeviceId();
  
  // Create a hash from the persistent ID
  let hash = 0;
  for (let i = 0; i < persistentDeviceId.length; i++) {
    const char = persistentDeviceId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  
  return 'DEV' + Math.abs(hash).toString(16).toUpperCase();
}

// Get device type
function getDeviceType() {
  const ua = navigator.userAgent;
  if (/tablet|ipad|playbook|silk/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
}

// Get device name
function getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/iPad/.test(ua)) return 'iPad';
  if (/Android/.test(ua)) {
    const match = ua.match(/Android[^;]*;\s*([^)]+)/);
    return match ? match[1].trim() : 'Android Device';
  }
  if (/Windows/.test(ua)) return 'Windows PC';
  if (/Mac/.test(ua)) return 'Mac';
  return 'Unknown Device';
}

export default function StaffClockPage() {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark');
    }
    return false;
  });
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [staff, setStaff] = useState(null);
  const [token, setToken] = useState(null);
  
  // Clock state
  const [clockStatus, setClockStatus] = useState(null);
  const [todaySchedule, setTodaySchedule] = useState(null);
  const [deviceRegistered, setDeviceRegistered] = useState(false);
  const [myDevice, setMyDevice] = useState(null);
  
  // Network state
  const [networkSSID, setNetworkSSID] = useState(null);
  const [checkingNetwork, setCheckingNetwork] = useState(false);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('clock'); // clock, schedule, device
  const [loginForm, setLoginForm] = useState({ email: '', pin: '' });
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Device info
  const deviceFingerprint = generateDeviceFingerprint();
  const deviceType = getDeviceType();
  const deviceName = getDeviceName();

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      const existingToken = localStorage.getItem('staff_token');
      const existingInfo = localStorage.getItem('staff_info');
      
      if (existingToken && existingInfo) {
        try {
          // Verify the token is still valid
          const res = await api.get(`/staff-portal/profile?token=${existingToken}`);
          const data = res.data;
          setStaff({
            id: data.id,
            name: data.name,
            email: data.email,
            role: data.role
          });
          setToken(existingToken);
          setIsAuthenticated(true);
        } catch (err) {
          // Token expired, clear it
          localStorage.removeItem('staff_token');
          localStorage.removeItem('staff_info');
        }
      }
      setLoading(false);
    };
    
    checkExistingSession();
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    if (newDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch clock status
  const fetchClockStatus = useCallback(async () => {
    if (!staff?.id || !token) return;
    
    try {
      const response = await api.get(`/workforce/timeclock/current/${staff.id}`);
      setClockStatus(response.data);
    } catch (error) {
      console.error('Error fetching clock status:', error);
    }
  }, [staff, token]);

  // Fetch today's schedule
  const fetchTodaySchedule = useCallback(async () => {
    if (!staff?.id || !token) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get(`/workforce/scheduling/staff/${staff.id}?date_from=${today}&date_to=${today}`);
      setTodaySchedule(response.data.schedules?.[0] || null);
    } catch (error) {
      console.error('Error fetching schedule:', error);
    }
  }, [staff, token]);

  // Fetch device status
  const fetchDeviceStatus = useCallback(async () => {
    if (!staff?.id || !token) return;
    
    try {
      const response = await api.get(`/workforce/timeclock/device/my-devices/${staff.id}`);
      const data = response.data;
      const activeDevices = data.devices?.filter(d => d.is_active) || [];
      setDeviceRegistered(activeDevices.length > 0);
      setMyDevice(activeDevices[0] || null);
    } catch (error) {
      console.error('Error fetching device status:', error);
    }
  }, [staff, token]);

  // Load data when authenticated
  useEffect(() => {
    if (isAuthenticated && staff && token) {
      fetchClockStatus();
      fetchTodaySchedule();
      fetchDeviceStatus();
      
      // Refresh every 30 seconds
      const interval = setInterval(() => {
        fetchClockStatus();
      }, 30000);
      
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, staff, token, fetchClockStatus, fetchTodaySchedule, fetchDeviceStatus]);

  // Login handler
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Use dedicated staff-login endpoint
      const response = await api.post('/workforce/timeclock/staff-login', {
        email: loginForm.email,
        pin: loginForm.pin.length === 6 && /^\d+$/.test(loginForm.pin) ? loginForm.pin : null,
        password: loginForm.pin.length !== 6 || !/^\d+$/.test(loginForm.pin) ? loginForm.pin : null
      });
      
      const data = response.data;
      const staffData = {
        id: data.id,
        name: data.name,
        email: data.email,
        role: data.role
      };
      setStaff(staffData);
      setToken(data.access_token);
      setIsAuthenticated(true);
      
      // Save to localStorage for session persistence
      localStorage.setItem('staff_token', data.access_token);
      localStorage.setItem('staff_info', JSON.stringify(staffData));
      
      toast.success(`Welcome, ${data.name}!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    }
    
    setLoading(false);
  };

  // Logout handler
  const handleLogout = () => {
    setIsAuthenticated(false);
    setStaff(null);
    setToken(null);
    setClockStatus(null);
    setTodaySchedule(null);
    setDeviceRegistered(false);
    setMyDevice(null);
    setLoginForm({ email: '', pin: '' });
    // Don't clear localStorage here - they might want to stay logged into /staff/profile
  };

  // Register device
  const handleRegisterDevice = async () => {
    setLoading(true);
    
    try {
      await api.post('/workforce/timeclock/device/register', {
        staff_id: staff.id,
        device_fingerprint: deviceFingerprint,
        device_name: deviceName,
        device_type: deviceType,
        browser_info: navigator.userAgent
      });
      
      toast.success('Device registered successfully!');
      fetchDeviceStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to register device');
    }
    
    setLoading(false);
  };

  // Clock in
  const handleClockIn = async () => {
    setLoading(true);
    
    try {
      // STEP 1: Verify network before allowing clock-in
      setCheckingNetwork(true);
      const networkCheck = await api.post('/staff-portal/verify-network');
      setCheckingNetwork(false);
      
      if (!networkCheck.data.is_on_cafe_network) {
        toast.error(networkCheck.data.message || 'You must be on the cafe WiFi (BC-Namaka-S) to clock in');
        setLoading(false);
        return;
      }
      
      // Set the network name from server response
      const detectedNetwork = networkCheck.data.network_name || 'BC-Namaka-S';
      setNetworkSSID(detectedNetwork);
      
      // STEP 2: Proceed with clock-in
      const response = await api.post('/workforce/timeclock/clock-in', {
        staff_id: staff.id,
        device_fingerprint: deviceFingerprint,
        network_ssid: detectedNetwork,
        ip_address: networkCheck.data.client_ip,
        location: null
      });
      
      const data = response.data;
      toast.success(data.message);
      
      if (data.early_clock_in) {
        toast.info(`Note: Paid time starts at ${data.entry.scheduled_start}`);
      }
      
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }
      
      fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clock in');
    }
    
    setLoading(false);
  };

  // Clock out
  const handleClockOut = async () => {
    if (!clockStatus?.entry?.id) {
      toast.error('No active clock-in found. Please refresh the page.');
      fetchClockStatus();
      return;
    }
    
    setLoading(true);
    
    try {
      console.log('Clock out request:', {
        entry_id: clockStatus.entry.id,
        device_fingerprint: deviceFingerprint
      });
      
      const response = await api.post('/workforce/timeclock/clock-out', {
        entry_id: clockStatus.entry.id,
        device_fingerprint: deviceFingerprint,
        notes: null
      });
      
      const data = response.data;
      toast.success(data.message);
      
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([100, 50, 100]);
      }
      
      // Refresh clock status to update UI
      await fetchClockStatus();
    } catch (error) {
      console.error('Clock out error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to clock out';
      toast.error(errorMsg);
      
      // If device mismatch, suggest re-registering
      if (errorMsg.includes('Device mismatch')) {
        toast.info('You may need to re-register this device', { duration: 5000 });
      }
    }
    
    setLoading(false);
  };

  // Start break (lunch, etc.)
  const handleStartBreak = async (breakType = 'lunch') => {
    if (!clockStatus?.entry?.id) {
      toast.error('No active clock-in found');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/workforce/timeclock/start-break', {
        entry_id: clockStatus.entry.id,
        device_fingerprint: deviceFingerprint,
        break_type: breakType
      });
      
      toast.success(response.data.message);
      
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([50, 100, 50]);
      }
      
      await fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start break');
    }
    
    setLoading(false);
  };

  // End break
  const handleEndBreak = async () => {
    if (!clockStatus?.entry?.id) {
      toast.error('No active clock-in found');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await api.post('/workforce/timeclock/end-break', {
        entry_id: clockStatus.entry.id,
        device_fingerprint: deviceFingerprint
      });
      
      toast.success(response.data.message);
      
      // Haptic feedback
      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }
      
      await fetchClockStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to end break');
    }
    
    setLoading(false);
  };

  // Format time
  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      month: 'long', 
      day: 'numeric'
    });
  };

  // Login screen
  if (!isAuthenticated) {
    // Show loading while checking session
    if (loading) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-amber-50 dark:bg-gray-900">
          <div className="text-center">
            <Clock className="w-12 h-12 text-teal-600 dark:text-teal-400 mx-auto animate-pulse" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
          </div>
        </div>
      );
    }
    
    return (
      <div className={`min-h-screen flex flex-col ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-teal-50 to-amber-50'}`}>
        {/* Header */}
        <header className="p-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-6 h-6 text-teal-600 dark:text-teal-400" />
            <span className="font-bold text-gray-800 dark:text-white">Staff Clock</span>
          </div>
          <button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow"
          >
            {isDarkMode ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
          </button>
        </header>

        {/* Login Form */}
        <main className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                  <User className="w-8 h-8 text-teal-600 dark:text-teal-400" />
                </div>
                <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Staff Login</h1>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Clock in to start your shift</p>
              </div>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                  <Input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="your.email@bulaccinocafe.com"
                    required
                    className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Password / PIN</label>
                  <Input
                    type="password"
                    value={loginForm.pin}
                    onChange={(e) => setLoginForm(prev => ({ ...prev, pin: e.target.value }))}
                    placeholder="Enter your password or PIN"
                    required
                    className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 text-lg bg-teal-600 hover:bg-teal-700"
                  data-testid="staff-login-btn"
                >
                  {loading ? 'Logging in...' : 'Login'}
                </Button>
              </form>

              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Smartphone className="w-4 h-4" />
                  <span>Device: {deviceName}</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Main clock interface
  return (
    <div className={`min-h-screen flex flex-col ${isDarkMode ? 'dark bg-gray-900' : 'bg-gradient-to-br from-teal-50 to-amber-50'}`}>
      {/* Header */}
      <header className="p-4 bg-white dark:bg-gray-800 shadow-sm">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
              <User className="w-5 h-5 text-teal-600 dark:text-teal-400" />
            </div>
            <div>
              <p className="font-medium text-gray-800 dark:text-white">{staff?.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{staff?.role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700"
            >
              {isDarkMode ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-gray-600" />}
            </button>
            <button
              onClick={handleLogout}
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="flex gap-2 p-2 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        {[
          { id: 'clock', label: 'Clock', icon: Clock },
          { id: 'schedule', label: 'Schedule', icon: Calendar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setView(tab.id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-sm font-medium transition-colors
              ${view === tab.id 
                ? 'bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400' 
                : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            data-testid={`staff-tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </nav>

      {/* Main Content */}
      <main className="flex-1 p-4 overflow-y-auto">
        {/* Clock View */}
        {view === 'clock' && (
          <div className="space-y-6">
            {/* Time Display */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-lg">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{formatDate(currentTime)}</p>
              <p className="text-5xl font-bold text-gray-800 dark:text-white font-mono">
                {formatTime(currentTime)}
              </p>
            </div>

            {/* Status Card */}
            <div className={`rounded-2xl p-6 shadow-lg ${
              clockStatus?.is_clocked_in 
                ? 'bg-green-500 dark:bg-green-600' 
                : 'bg-white dark:bg-gray-800'
            }`}>
              {clockStatus?.is_clocked_in ? (
                <div className="text-center text-white">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3" />
                  <p className="text-lg font-medium">Currently Working</p>
                  <p className="text-3xl font-bold mt-2">
                    {clockStatus.entry?.current_hours?.toFixed(2) || '0.00'} hrs
                  </p>
                  <p className="text-sm opacity-80 mt-1">
                    Started at {new Date(clockStatus.entry?.clock_in_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                    <Clock className="w-6 h-6 text-gray-400" />
                  </div>
                  <p className="text-lg font-medium text-gray-800 dark:text-white">Not Clocked In</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Ready to start your shift?</p>
                </div>
              )}
            </div>

            {/* Today's Schedule */}
            {todaySchedule && (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-2">
                  <Calendar className="w-4 h-4" />
                  <span>Today&apos;s Shift</span>
                </div>
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-lg font-medium text-gray-800 dark:text-white">
                      {todaySchedule.start_time} - {todaySchedule.end_time}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {todaySchedule.shift_template_name || 'Regular Shift'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-teal-600 dark:text-teal-400">
                      {todaySchedule.hours?.toFixed(1)} hrs
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Clock In/Out Button */}
            <div className="pt-4">
              {clockStatus?.is_clocked_in ? (
                <div className="space-y-3">
                  {/* Show if on break */}
                  {clockStatus?.on_break ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-2xl p-4 text-center mb-4">
                      <Timer className="w-8 h-8 mx-auto mb-2 text-yellow-600 dark:text-yellow-400 animate-pulse" />
                      <p className="text-yellow-800 dark:text-yellow-300 font-medium">On Break</p>
                      {clockStatus?.entry?.current_break_minutes && (
                        <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                          {Math.floor(clockStatus.entry.current_break_minutes)} min
                        </p>
                      )}
                    </div>
                  ) : null}
                  
                  {/* Break/Resume buttons when clocked in */}
                  {clockStatus?.on_break ? (
                    <Button
                      onClick={handleEndBreak}
                      disabled={loading}
                      className="w-full h-14 text-lg bg-green-500 hover:bg-green-600"
                      data-testid="end-break-btn"
                    >
                      {loading ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Play className="w-5 h-5 mr-2" />
                          End Break - Back to Work
                        </>
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={() => handleStartBreak('lunch')}
                      disabled={loading}
                      className="w-full h-14 text-lg bg-yellow-500 hover:bg-yellow-600 text-white"
                      data-testid="start-break-btn"
                    >
                      {loading ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Timer className="w-5 h-5 mr-2" />
                          Start Lunch Break
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Clock out button - always visible when clocked in */}
                  {!clockStatus?.on_break && (
                    <Button
                      onClick={handleClockOut}
                      disabled={loading}
                      className="w-full h-14 text-lg bg-red-500 hover:bg-red-600"
                      data-testid="clock-out-btn"
                    >
                      {loading ? (
                        <RefreshCw className="w-5 h-5 animate-spin" />
                      ) : (
                        <>
                          <Square className="w-5 h-5 mr-2" />
                          Clock Out - End Shift
                        </>
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <Button
                  onClick={handleClockIn}
                  disabled={loading}
                  className="w-full h-16 text-xl bg-green-500 hover:bg-green-600"
                  data-testid="clock-in-btn"
                >
                  {loading ? (
                    <RefreshCw className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      Clock In
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Schedule View */}
        {view === 'schedule' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-gray-800 dark:text-white">Your Schedule</h2>
            
            {todaySchedule ? (
              <div 
                className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-lg border-l-4"
                style={{ borderLeftColor: todaySchedule.color || '#14b8a6' }}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Today</p>
                    <p className="text-lg font-medium text-gray-800 dark:text-white mt-1">
                      {todaySchedule.start_time} - {todaySchedule.end_time}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      {todaySchedule.shift_template_name || 'Regular Shift'}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="px-3 py-1 rounded-full text-sm font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400">
                      {todaySchedule.hours?.toFixed(1)} hrs
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 text-center shadow-lg">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
                <p className="text-gray-500 dark:text-gray-400">No shift scheduled for today</p>
              </div>
            )}

            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Check with your manager for weekly schedule updates
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="p-4 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Clock className="w-4 h-4" />
          <span>Bulaccino Cafe Staff Portal</span>
        </div>
      </footer>
    </div>
  );
}
